import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DialerEventResultSchema, type DialerEventBody, type DialerEventResult } from '@shared/schemas/terra-signals';
import { useApi } from '../../../lib/api';
import { ApiProblemError, WriteRefusedError, fetchJson } from '../../../lib/apiFetchJson';
import { completedStepNumber, isConfirmedDone, isConfirmedRemoval, type TrellusTaskEvent } from '../../../lib/trellus/host-schemas';
import { TRELLUS_TOMBSTONE_CAP, expireTrellusTombstone, useTrellusHostStore } from '../../../lib/trellus/trellus-host-store';
import { invalidateSignalQueries } from '../signals/useSignalsMutations';

/**
 * `useTrellusCompletionSync` (gong reconcile U20, plan R7 / D8). Mounted once
 * by `TrellusHostConnection`. For each NEW task event in the host store that
 * is a confirmed completion (`task_completed` / `task_already_completed` with
 * `completion_status: completed`) or a confirmed `flow_removed`, and that
 * names a `clay_signal_id`, posts one dialer-event hint so the server
 * re-checks that row through reconcile (U18). The row is already hidden by
 * the store's tombstone. The server's answer then drives the page
 * (`describeDialerOutcome`): a row the server really closed or hid is
 * re-fetched out of the lists (so a Refresh never brings it back — default
 * views filter `action_state = 'To do'`) and the rep gets a plain toast; a
 * `unchanged` answer keeps the tombstone as the only thing hiding the row.
 * The step is Gong's, carried from the event (`gong_step_number`); the server
 * compares it with the mirror. Analytics are emitted server-side.
 *
 * Dedupe: a ref set of seen event ids, reset (and seeded with the events
 * already in the store) at the top of the mount effect — StrictMode's
 * setup → cleanup → setup posts once, a remount never re-posts, and a replay
 * of an event the 100-entry store buffer already evicted posts zero times.
 */

/** Shown when the hint fails without server text (network, timeout, malformed answer). */
export const TRELLUS_SYNC_FALLBACK_MESSAGE = 'Couldn’t update this task. Salesforce will catch up.';

export interface DialerOutcomeNotice {
  kind: 'success' | 'info';
  title: string;
  description: string;
}

export interface DialerOutcomeEffect {
  /** Re-fetch the signal lists: the server really changed (or found changed) the row. */
  invalidate: boolean;
  /** `null` = silent. */
  notice: DialerOutcomeNotice | null;
}

const BACK_BY_FORMAT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });

/**
 * What the page does with the server's dialer-event answer. Pure, for tests.
 * `already_closed` / `already_snoozed` are silent (nothing happened just now);
 * `already_closed` still re-fetches since the list may be stale.
 */
export function describeDialerOutcome(result: DialerEventResult): DialerOutcomeEffect {
  switch (result.outcome) {
    case 'closed':
      return { invalidate: true, notice: { kind: 'success', title: 'Task closed', description: 'Marked done in Salesforce.' } };
    case 'snoozed':
      return {
        invalidate: true,
        notice: {
          kind: 'info',
          title: 'Hidden until the next step',
          description: result.resurfaces ? `Back by ${BACK_BY_FORMAT.format(new Date(result.resurfaces))}` : 'Returns when Gong moves to the next step.',
        },
      };
    case 'already_closed':
      return { invalidate: true, notice: null };
    case 'already_snoozed':
      return { invalidate: false, notice: null };
    case 'unchanged':
      return {
        invalidate: false,
        notice: { kind: 'info', title: 'Still syncing with Gong', description: 'Hidden for now. Salesforce catches up on the next check.' },
      };
  }
}

function dialerKind(event: TrellusTaskEvent): DialerEventBody['kind'] | null {
  if (!isConfirmedDone(event)) return null;
  return isConfirmedRemoval(event) ? 'flow_removed' : 'task_completed';
}

/**
 * The tombstone is the only local hiding. A dated `snoozed` answer sets it to expire at the resurface date (the server
 * hides the row until then; left unexpired it would keep the row hidden past it). `closed` keeps it (a closed row never
 * reopens); an undated snooze keeps it (the step rule reveals the row when Gong moves on); `unchanged`, `already_*` and
 * failures leave it untouched. The lists are refetched either way, plain: nothing depends on the refetch succeeding.
 */
function announce(result: DialerEventResult, queryClient: ReturnType<typeof useQueryClient>, event: TrellusTaskEvent): void {
  const { invalidate, notice } = describeDialerOutcome(result);
  if (result.outcome === 'snoozed' && result.resurfaces && event.clay_signal_id) {
    const resurfaceAt = Date.parse(result.resurfaces);
    if (Number.isFinite(resurfaceAt)) expireTrellusTombstone(event.clay_signal_id, event.event_id, resurfaceAt);
  }
  if (invalidate) void invalidateSignalQueries(queryClient).catch(() => undefined);
  if (notice === null) return;
  if (notice.kind === 'success') toast.success(notice.title, { description: notice.description });
  else toast(notice.title, { description: notice.description });
}

/**
 * A failed hint never touches the store: the tombstone keeps the row hidden
 * for this session and reconcile closes it later. A write refusal
 * (impersonation, external-write sandbox) was already announced by `apiFetch`.
 */
function toastSyncFailure(err: unknown): void {
  if (err instanceof WriteRefusedError) return;
  if (err instanceof ApiProblemError && err.kind === 'http') {
    if (err.title) {
      toast.error(err.title, err.detail ? { description: err.detail } : undefined);
      return;
    }
    if (err.userMessage) {
      toast.error(err.userMessage);
      return;
    }
  }
  toast.error(TRELLUS_SYNC_FALLBACK_MESSAGE);
}

function toBody(event: TrellusTaskEvent, kind: DialerEventBody['kind']): DialerEventBody {
  const step = completedStepNumber(event);
  return {
    kind,
    eventId: event.event_id,
    ...(event.gong_task_id ? { gongTaskId: event.gong_task_id } : {}),
    ...(event.gong_flow_instance_id ? { gongFlowInstanceId: event.gong_flow_instance_id } : {}),
    ...(kind === 'task_completed' && step !== null ? { gongStepNumber: step } : {}),
  };
}

export function useTrellusCompletionSync(): void {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const seen = useRef<Set<string>>(new Set());
  useEffect(() => {
    // StrictMode rule: reset at the top of the mount effect. Seeded with the
    // events already in the store so a (re)mount only posts what arrives after it.
    seen.current = new Set(useTrellusHostStore.getState().task_event_ids);
    return useTrellusHostStore.subscribe(state => {
      for (const id of state.task_event_ids) {
        if (seen.current.has(id)) continue;
        seen.current.add(id);
        if (seen.current.size > TRELLUS_TOMBSTONE_CAP) {
          const oldest = seen.current.values().next().value;
          if (oldest !== undefined) seen.current.delete(oldest);
        }
        const event = state.task_events[id];
        const kind = event?.clay_signal_id ? dialerKind(event) : null;
        if (!event?.clay_signal_id || !kind) continue;
        void fetchJson(apiFetch, DialerEventResultSchema, `/api/flow/signals/${encodeURIComponent(event.clay_signal_id)}/dialer-event`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(toBody(event, kind)),
        }).then(result => announce(result, queryClient, event), toastSyncFailure);
      }
    });
  }, [apiFetch, queryClient]);
}
